import type { EditorValidationError, LinkPayload, PackageWithLinksResult } from "@models/ISchema";
import type { ValidationMessageParams, ValidationPresentation } from "../../types";
import type { OrderedSubjectSnapshot } from "../package-with-leagues-mappers";
import { resolveValidationField, resolveValidationMessage } from "../validation-message";

export interface SubmissionValidations {
  byCourseId: Record<string, ValidationPresentation[]>;
  global: ValidationPresentation[];
}

const discardableGroupReasons = new Set<string>([
  "GROUP_CODE_ALREADY_TAKEN",
  "NUMERIC_GROUP_CODE_VALIDATION",
  "INVALID_GROUP",
  "COULD_NOT_CREATE_GROUP",
]);

const getReason = (validation: object): string | null => {
  if ("reason" in validation && validation.reason) {
    return String(validation.reason);
  }
  if ("code" in validation && validation.code) {
    return String(validation.code);
  }
  return null;
};

interface PresentationOptions {
  hasInitialGroup?: boolean;
  params?: ValidationMessageParams;
}

const createPresentation = (
  reason: string,
  scope: ValidationPresentation["scope"],
  discardable: boolean,
  { hasInitialGroup = false, params }: PresentationOptions = {},
): ValidationPresentation => {
  const field = resolveValidationField(reason, hasInitialGroup);

  return {
    reason,
    message: resolveValidationMessage(reason, params),
    scope,
    discardable,
    ...(field ? { field } : {}),
    ...(params && Object.keys(params).length ? { params } : {}),
  };
};

const getValidationParams = (validation: object): ValidationMessageParams | undefined => {
  if ("capacity" in validation && typeof validation.capacity === "number") {
    return { capacity: validation.capacity };
  }

  return undefined;
};

const flattenLinkValidation = (
  validation: EditorValidationError,
  subject: OrderedSubjectSnapshot,
): ValidationPresentation[] => {
  if (validation.__typename === "InvalidSectionChanges") {
    const nestedValidations = validation.validationErrors ?? [];
    if (!nestedValidations.length) {
      return [createPresentation("INVALID_SECTION_CHANGES", "subject", false)];
    }

    return nestedValidations
      .filter(nestedValidation => Boolean(nestedValidation))
      .map(nestedValidation =>
        createPresentation(
          getReason(nestedValidation) ?? nestedValidation.__typename ?? "UNKNOWN_VALIDATION",
          "subject",
          false,
          {
            hasInitialGroup: subject.hasInitialGroup,
            params: getValidationParams(nestedValidation),
          },
        ),
      );
  }

  const reason = getReason(validation) ?? validation.__typename ?? "UNKNOWN_VALIDATION";
  const isDiscardableGroupValidation =
    subject.hasInitialGroup &&
    (validation.__typename === "InvalidGroupCodeValidation" || discardableGroupReasons.has(reason));

  return [
    createPresentation(
      reason,
      isDiscardableGroupValidation ? "group" : "subject",
      isDiscardableGroupValidation,
      { hasInitialGroup: subject.hasInitialGroup, params: getValidationParams(validation) },
    ),
  ];
};

const packageValidationToPresentation = (
  validation: EditorValidationError,
): ValidationPresentation =>
  createPresentation(
    getReason(validation) ?? validation.__typename ?? "UNKNOWN_VALIDATION",
    "global",
    false,
  );

const getLinkPayloads = (result: PackageWithLinksResult): Array<LinkPayload | null> =>
  (result.payload?.links ?? []) as Array<LinkPayload | null>;

export const mapSubmissionValidations = (
  result: PackageWithLinksResult,
  subjects: OrderedSubjectSnapshot[],
): SubmissionValidations => {
  const global = (result.payload?.package?.validationErrors ?? [])
    .filter(validation => Boolean(validation))
    .map(validation => packageValidationToPresentation(validation));
  const byCourseId: Record<string, ValidationPresentation[]> = {};
  const linkPayloads = getLinkPayloads(result);

  if (linkPayloads.length !== subjects.length) {
    global.push(createPresentation("PACKAGE_LINK_RESPONSE_ORDER_MISMATCH", "global", false));
    return { byCourseId, global };
  }

  subjects.forEach((subject, index) => {
    const validations = (linkPayloads[index]?.validationErrors ?? [])
      .filter(validation => Boolean(validation))
      .flatMap(validation => flattenLinkValidation(validation, subject));

    if (validations.length) byCourseId[subject.courseId] = validations;
  });

  return { byCourseId, global };
};

export const hasSubmissionValidations = ({ byCourseId, global }: SubmissionValidations): boolean =>
  global.length > 0 || Object.values(byCourseId).some(messages => messages.length > 0);

export const getUnresolvedDiscardableCourseIds = (
  validationMessagesByCourseId: Record<string, ValidationPresentation[]>,
): Set<string> =>
  new Set(
    Object.entries(validationMessagesByCourseId)
      .filter(
        ([, messages]) => messages.length > 0 && messages.every(message => message.discardable),
      )
      .map(([courseId]) => courseId),
  );

export const hasBlockingValidationMessages = ({
  validationMessagesByCourseId,
  globalValidationMessages,
}: {
  validationMessagesByCourseId: Record<string, ValidationPresentation[]>;
  globalValidationMessages: ValidationPresentation[];
}): boolean =>
  globalValidationMessages.some(message => !message.discardable) ||
  Object.values(validationMessagesByCourseId).some(messages =>
    messages.some(message => !message.discardable),
  );
